import { faPlus, faTrash, faTrashAlt, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGStocksDetailsForm, CGStocksUploadDetailsForm } from ".";
import {
	AcquisitionOptions,
	AssetsTypesOptions,
	StockAssetsTypes
} from "../../../config/filingConstants";
import { IFormITR1, IStocksForm, IStocksFormTypes } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const CGStocksDetails: React.FC = () => {
	const [showAddStocksCanvas, setShowAddStocksCanvas] = useState<IStocksFormTypes>(null);
	const [showDeleteModal, setShowDeleteModal] = useState<IStocksForm | null>(null);
	const [activeItem, setActiveItem] = useState<IStocksForm | null>(null);

	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const StocksDestails = cloneDeep(values.IncomeDetails?.Stocks || []);

	const handleDeleteStockItem = async () => {
		StocksDestails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.Stocks", StocksDestails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		let TotalListedLong = 0;
		let TotalListedShort = 0;
		let TotalUnlistedLong = 0;
		let TotalUnlistedShort = 0;
		for (let index = 0; index < StocksDestails.length; index++) {
			const stock = StocksDestails[index];
			if (stock.TypeAssets === StockAssetsTypes.Listed_Long_10_Per) {
				TotalListedLong += Number(stock.CapitalGains);
			} else if (stock.TypeAssets === StockAssetsTypes.Listed_Short_15_Per) {
				TotalListedShort += Number(stock.CapitalGains);
			} else if (stock.TypeAssets === StockAssetsTypes.Unlisted_Normal) {
				TotalUnlistedLong += Number(stock.CapitalGains);
			} else if (stock.TypeAssets === StockAssetsTypes.Unlisted_Short_20_Per) {
				TotalUnlistedShort += Number(stock.CapitalGains);
			}
		}

		let _TotalSplRateTax = 0;
		let _TotalNrlRateIncome = 0;
		// in excess of 1 lakh
		if (TotalListedLong > 100000) {
			const excessAmount = TotalListedLong - 100000;
			_TotalSplRateTax += excessAmount * 0.1;
		}

		_TotalSplRateTax += TotalListedShort * 0.15;

		_TotalSplRateTax += TotalUnlistedShort * 0.2;

		_TotalNrlRateIncome = TotalUnlistedLong;

		const StocksGains =
			StocksDestails?.reduce((acc, curr) => {
				const value = parseInt(curr.CapitalGains);
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		const CapitalGains = {
			...values.CapitalGains,
			TotalStocksGain: StocksGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		const CapitalGainsSplRateTax = {
			...values.CapitalGainsSplRateTax,
			StocksSplRateTax: _TotalSplRateTax
		};

		const CapitalGainsNrlRateIncome = {
			...values.CapitalGainsNrlRateIncome,
			StocksNrlRateIncome: _TotalNrlRateIncome
		};

		const TotalCGSplRtTax = Object.values(CapitalGainsSplRateTax)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const TotalCGNrlRtInc = Object.values(CapitalGainsNrlRateIncome)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const SPL_DATA = {
			CapitalGainsSplRateTax: { ...CapitalGainsSplRateTax },
			CapitalGainsNrlRateIncome: { ...CapitalGainsNrlRateIncome },
			TotalSplRateTax: TotalCGSplRtTax,
			TotalNrlRateIncome: TotalCGNrlRtInc
		};

		setValues({ ...cloneDeep(values), ...DATA, ...SPL_DATA });
		setFieldValue("IncomeDetails.Stocks", StocksDestails);
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>Stocks</h5>
					<PreventEditComponent>
						<div className="btns-wrap">
							<Button
								variant="primaryDark"
								size="sm"
								onClick={() => setShowAddStocksCanvas(() => "UPLOAD")}
							>
								<FASvgIcon icon={faUpload} />
								Upload
							</Button>
							<Button
								variant="secondary"
								size="sm"
								onClick={() => setShowAddStocksCanvas(() => "MANUAL")}
							>
								<FASvgIcon icon={faPlus} />
								Add
							</Button>
						</div>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{StocksDestails?.map((stock, index) => {
						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>
									{AssetsTypesOptions[stock.TypeAssets]}
									{stock.Acquisition ? <>&nbsp;({AcquisitionOptions[stock.Acquisition]})</> : null}
								</dt>
								<dd>
									<span>
										<PriceDisplay price={stock?.CapitalGains} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddStocksCanvas(() => "MANUAL");
											setActiveItem(() => stock);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>

									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => stock);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>

			<CGStocksDetailsForm
				showCanvas={showAddStocksCanvas === "MANUAL"}
				formValues={activeItem}
				handleCloseBtnPress={(stocksData) => {
					if (stocksData) {
						if (activeItem) {
							StocksDestails.splice(Number(activeItemIdx), 1, stocksData);
						} else {
							StocksDestails.push(stocksData);
						}
						handleTotalCalculation();
					}
					setShowAddStocksCanvas(() => null);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
			/>

			<CGStocksUploadDetailsForm
				showCanvas={showAddStocksCanvas === "UPLOAD"}
				formType={showAddStocksCanvas}
				handleCloseBtnPress={(stocksData) => {
					if (stocksData) {
						const longTerm = { ...stocksData.long_term };
						const shortTerm = { ...stocksData.short_term };
						StocksDestails.push(longTerm);
						StocksDestails.push(shortTerm);
						handleTotalCalculation();
					}
					setShowAddStocksCanvas(() => null);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={
					<p>
						{showDeleteModal ? (
							<>
								You want to delete {AssetsTypesOptions[showDeleteModal.TypeAssets]}
								{showDeleteModal.Acquisition ? (
									<>&nbsp;({AcquisitionOptions[showDeleteModal.Acquisition]})</>
								) : null}
							</>
						) : null}
					</p>
				}
				onYesBtnClick={() => handleDeleteStockItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default CGStocksDetails;
